import { useCallback, useEffect, useRef } from 'react';
import { CanvasMode } from '../../BoardCanvas.types';
import { EditableElement, TextStyle } from '../../editing/types';
import { UseCanvasStateReturn } from './useCanvasState';

export function useCanvasHelpers(
  state: UseCanvasStateReturn,
  onModeChange?: (mode: CanvasMode) => void
) {
  const {
    containerRef,
    canvasRef,
    mode,
    setMode,
    createMode,
    connectorMode,
    isCreating,
    clearSelection,
    elements,
    textEditor,
  } = state;

  // Handle mode change
  const handleModeChange = useCallback((newMode: CanvasMode) => {
    setMode(newMode);
    onModeChange?.(newMode);
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const layer = canvas.getLayer();
    const isEdit = newMode === 'edit';
    
    // Update all nodes' draggable state
    layer.children?.forEach((node) => {
      if (node.id() && node !== layer) {
        node.draggable(isEdit);
      }
    });
    layer.batchDraw();
    
    if (newMode === 'view') {
      clearSelection();
      canvas.setPanningDisabled(false);
    } else {
      canvas.setPanningDisabled(true);
    }
  }, [onModeChange, clearSelection, setMode, canvasRef]);

  // Handle double click for text editing
  const handleDoubleClick = useCallback((element: EditableElement) => {
    if (createMode.type !== 'select') return;
    
    const isShape = element.type === 'shape';
    const textColor = isShape ? (element.data?.textFill || '#000000') : (element.data?.fill || '#000000');
    
    const style: TextStyle = {
      fontSize: element.data?.fontSize || 16,
      fontFamily: element.data?.fontFamily || 'Arial',
      fontStyle: element.data?.fontStyle || 'normal',
      fill: textColor,
      align: element.data?.align || 'left',
      lineHeight: 1.2,
      letterSpacing: 0,
    };
    textEditor.startEditing(element, style);
  }, [createMode, textEditor]);

  // Handle element double-click for text editing (custom event from canvas)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    const handleElementDblClick = (e: Event) => {
      const customEvent = e as CustomEvent<EditableElement>;
      if (customEvent.detail) {
        handleDoubleClick(customEvent.detail);
      }
    };
    
    container.addEventListener('elementdblclick', handleElementDblClick);
    return () => container.removeEventListener('elementdblclick', handleElementDblClick);
  }, [handleDoubleClick, containerRef]);

  // Hide/show text when text editor opens/closes
  const previousEditingIdRef = useRef<string | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const currentEditingId = textEditor.editingState.editingTextId;
    const previousEditingId = previousEditingIdRef.current;
    
    // If we stopped editing an element, show its text again
    if (previousEditingId && previousEditingId !== currentEditingId) {
      const previousRenderer = canvas.getRenderer(previousEditingId);
      if (previousRenderer && 'showText' in previousRenderer) {
        (previousRenderer as any).showText();
        canvas.batchDraw();
      }
    }
    
    // If we started editing an element, hide its text
    if (currentEditingId) {
      const renderer = canvas.getRenderer(currentEditingId);
      if (renderer && 'hideText' in renderer) {
        (renderer as any).hideText();
        canvas.batchDraw();
      }
    }
    
    previousEditingIdRef.current = currentEditingId;
  }, [textEditor.editingState.editingTextId, canvasRef]);

  // Handle delete
  const handleDelete = useCallback((ids: string[]) => {
    // historyManager.deleteElements already calls setElements internally
    state.historyManagerRef.current?.deleteElements(ids);
    clearSelection();
  }, [clearSelection, state]);

  // Helper to get element screen position from Konva (source of truth)
  const getElementScreenBounds = useCallback((id: string) => {
    return canvasRef.current?.getElementScreenPosition(id) || null;
  }, [canvasRef]);

  // Helper to get multiple elements' combined screen bounds
  const getElementsScreenBounds = useCallback((ids: string[]) => {
    return canvasRef.current?.getElementsScreenBounds(ids) || null;
  }, [canvasRef]);

  // Get cursor style
  const getCursor = (): string => {
    if (mode === 'view') return 'default';
    if (connectorMode) return 'crosshair';
    if (createMode.type === 'pan') return 'grab';
    if (isCreating) return 'crosshair';
    if (createMode.type !== 'select') return 'crosshair';
    return 'default';
  };

  // Get editing element data from elements (for non-position properties like text, style)
  const editingElement = elements.find((el) => el.id === textEditor.editingState.editingTextId);
  
  // Get editing element screen position from Konva (for toolbar positioning)
  const editingElementBounds = getElementScreenBounds(textEditor.editingState.editingTextId || '');

  return {
    handleModeChange,
    handleDoubleClick,
    handleDelete,
    getElementScreenBounds,
    getElementsScreenBounds,
    getCursor,
    editingElement,
    editingElementBounds,
  };
}
